// task-command.ts
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";

// ---- Types ----

const taskTypeZod = z.enum(["SUGGEST", "EXTRACT_TAGS", "INFO_BLOCK"]);
type TaskType = z.infer<typeof taskTypeZod>;
const taskCommandZodSchema = z.object({
    _id: idZodSchema,
    _type: z.literal("TASK_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    task: taskTypeZod,
    input: z.record(z.string()),
    providerId: providerIdZodSchema.optional(),
});

type TaskCommand = z.infer<typeof taskCommandZodSchema>;

type TaskCommandFactoryParams = Except<
    TaskCommand,
    "_id" | "_type" | "_createdAt"
>;

const taskCommandFactory = (params: TaskCommandFactoryParams): TaskCommand => ({
    ...params,
    _id: generateId({ prefix: "TASK_COMMAND__" }),
    _type: "TASK_COMMAND",
    _createdAt: nowIsoAndUnix(),
});

const taskResponseSuggestionZodSchema = z.object({
    task: z.literal("SUGGEST"),
    message: z.string(),
    suggestions: z.array(z.string()),
});
const taskResponseInfoblockZodSchema = z.object({
    task: z.literal("INFO_BLOCK"),
    message: z.string(),
    suggestions: z.array(
        z.object({
            title: z.string(),
            content: z.string(),
        }),
    ),
});

const taskResponseZodSchema = z.discriminatedUnion("task", [
    taskResponseSuggestionZodSchema,
    taskResponseInfoblockZodSchema,
]);

type TaskResponse = z.infer<typeof taskResponseZodSchema>;

export { taskCommandFactory, taskCommandZodSchema, taskResponseZodSchema };
export type { TaskCommand, TaskCommandFactoryParams, TaskResponse, TaskType };
